import { useCallback, useEffect, useRef, useState } from 'react'

export type PlayerStatus = 'ready' | 'playing' | 'error'

type Source = 'youtube' | 'audio'

interface YTPlayer {
  seekTo: (seconds: number, allowSeekAhead: boolean) => void
  playVideo: () => void
  pauseVideo: () => void
  getPlayerState: () => number
  getCurrentTime: () => number
  getDuration: () => number
  destroy: () => void
}

interface YTNamespace {
  Player: new (
    el: HTMLElement,
    options: {
      videoId: string
      width: number
      height: number
      playerVars: Record<string, number | string>
      events: {
        onReady: () => void
        onStateChange: (e: { data: number }) => void
        onError: () => void
      }
    },
  ) => YTPlayer
}

declare global {
  interface Window {
    YT?: YTNamespace
    onYouTubeIframeAPIReady?: () => void
  }
}

// If YouTube hasn't actually started playing within this window (blocked autoplay,
// sandboxed frame, embed restrictions), fall back to the native audio source.
const YOUTUBE_WATCHDOG_MS = 2500
// Where every tap starts the song from, in seconds.
const START_AT_SECONDS = 3
const YT_PLAYING = 1
const YT_ENDED = 0

let apiPromise: Promise<YTNamespace> | null = null

function loadYouTubeApi(): Promise<YTNamespace> {
  if (window.YT?.Player) return Promise.resolve(window.YT)
  if (apiPromise) return apiPromise
  apiPromise = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      previous?.()
      if (window.YT) resolve(window.YT)
    }
    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    script.onerror = () => {
      apiPromise = null
      reject(new Error('YouTube API failed to load'))
    }
    document.head.appendChild(script)
  })
  return apiPromise
}

export function useThemePlayer(videoId: string, audioUrl: string) {
  const hostRef = useRef<HTMLDivElement>(null)
  const ytRef = useRef<YTPlayer | null>(null)
  const ytReadyRef = useRef(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const unlockedRef = useRef(false)
  const sourceRef = useRef<Source>('youtube')
  const watchdogRef = useRef<number>(0)
  const rafRef = useRef<number>(0)
  const [status, setStatus] = useState<PlayerStatus>('ready')
  const [progress, setProgress] = useState(0)

  const stopTicking = () => cancelAnimationFrame(rafRef.current)

  const tick = useCallback(() => {
    if (sourceRef.current === 'audio') {
      const a = audioRef.current
      if (a && a.duration > 0) setProgress(Math.min(a.currentTime / a.duration, 1))
    } else {
      const p = ytRef.current
      const d = p?.getDuration() ?? 0
      if (p && d > 0) setProgress(Math.min(p.getCurrentTime() / d, 1))
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [])

  const startTicking = useCallback(() => {
    stopTicking()
    rafRef.current = requestAnimationFrame(tick)
  }, [tick])

  const playAudio = useCallback(() => {
    const a = audioRef.current
    if (!a) return
    a.muted = false
    a.currentTime = START_AT_SECONDS
    setProgress(0)
    a.play().catch(() => setStatus('error'))
  }, [])

  // Native audio source (always available, lives in our own frame so taps can start it)
  useEffect(() => {
    const a = new Audio(audioUrl)
    a.preload = 'auto'
    audioRef.current = a
    const onPlaying = () => {
      if (a.muted || sourceRef.current !== 'audio') return
      setStatus('playing')
      startTicking()
    }
    const onEnded = () => {
      stopTicking()
      setProgress(0)
      setStatus('ready')
    }
    a.addEventListener('playing', onPlaying)
    a.addEventListener('ended', onEnded)
    return () => {
      a.pause()
      a.removeEventListener('playing', onPlaying)
      a.removeEventListener('ended', onEnded)
      audioRef.current = null
    }
  }, [audioUrl, startTicking])

  // YouTube source (full-length official theme)
  useEffect(() => {
    let cancelled = false
    const host = hostRef.current
    if (!host) return
    const mount = document.createElement('div')
    host.appendChild(mount)

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled) return
        ytRef.current = new YT.Player(mount, {
          videoId,
          width: 200,
          height: 200,
          playerVars: { controls: 0, playsinline: 1, rel: 0, disablekb: 1 },
          events: {
            onReady: () => {
              if (!cancelled) ytReadyRef.current = true
            },
            onStateChange: (e) => {
              if (sourceRef.current !== 'youtube') return
              if (e.data === YT_PLAYING) {
                window.clearTimeout(watchdogRef.current)
                setStatus('playing')
                startTicking()
              } else if (e.data === YT_ENDED) {
                stopTicking()
                setProgress(0)
                setStatus('ready')
              }
            },
            onError: () => {
              sourceRef.current = 'audio'
            },
          },
        })
      })
      .catch(() => {
        sourceRef.current = 'audio'
      })

    return () => {
      cancelled = true
      window.clearTimeout(watchdogRef.current)
      stopTicking()
      ytRef.current?.destroy()
      ytRef.current = null
      ytReadyRef.current = false
      host.innerHTML = ''
    }
  }, [videoId, startTicking])

  // Must be called synchronously from a tap/click handler.
  const play = useCallback(() => {
    window.clearTimeout(watchdogRef.current)
    const yt = ytRef.current

    if (sourceRef.current === 'youtube' && yt && ytReadyRef.current) {
      // Unlock the audio element inside this gesture so a later fallback is allowed to play.
      const a = audioRef.current
      if (a && !unlockedRef.current) {
        unlockedRef.current = true
        a.muted = true
        a.play()
          .then(() => {
            a.pause()
            a.currentTime = 0
            a.muted = false
          })
          .catch(() => {
            a.muted = false
          })
      }
      yt.seekTo(START_AT_SECONDS, true)
      yt.playVideo()
      setProgress(0)
      watchdogRef.current = window.setTimeout(() => {
        if (yt.getPlayerState() !== YT_PLAYING) {
          sourceRef.current = 'audio'
          yt.pauseVideo()
          playAudio()
        }
      }, YOUTUBE_WATCHDOG_MS)
      return
    }

    sourceRef.current = 'audio'
    playAudio()
  }, [playAudio])

  const stop = useCallback(() => {
    window.clearTimeout(watchdogRef.current)
    stopTicking()
    const yt = ytRef.current
    if (yt && ytReadyRef.current) {
      yt.pauseVideo()
      yt.seekTo(0, true)
    }
    const a = audioRef.current
    if (a) {
      a.pause()
      a.currentTime = 0
    }
    setProgress(0)
    setStatus((s) => (s === 'error' ? s : 'ready'))
  }, [])

  return { hostRef, status, progress, play, stop }
}
